/**
 * Helpers for applying the action cards the AI chat returns.
 *
 * buildPlannedDate was duplicated in aiChatAddSuggestion and aiChatReschedule;
 * it lives here so the single-card and "Apply all" paths format the date the
 * same way and can be tested without a Vuex harness.
 */

const { toBackendDate } = require('./documentImportDates');
const { RIDE_LANGUAGE_CODES } = require('../common/rideLanguageOptions');

// The backend reads 11:59 PM back as "no specific time" (ai_chat/api.py).
const NO_SPECIFIC_TIME = '11:59:00 PM';

/**
 * Build the planned-date string the ride API expects: "M/D/YYYY, hh:mm:ss AM".
 *
 * @param {Date|string} dayDateTime the calendar day the attraction moves to
 * @param {string} time "HH:MM" 24-hour, or "no specific time"/empty for the default
 */
function buildPlannedDate(dayDateTime, time) {
  const dt = new Date(dayDateTime);
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const y = dt.getFullYear();

  let timeStr = NO_SPECIFIC_TIME;
  if (time && time !== 'no specific time') {
    const [hStr, minStr] = time.split(':');
    const h = parseInt(hStr, 10);
    const min = parseInt(minStr, 10) || 0;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    timeStr = `${String(h12).padStart(2, '0')}:${String(min).padStart(2, '0')}:00 ${period}`;
  }

  return `${m}/${d}/${y}, ${timeStr}`;
}

/**
 * Apply items one at a time, keeping going past a failure.
 *
 * Sequential on purpose: these are writes to rows of the same ride, and a card
 * that fails should leave the others applied rather than abort the batch.
 *
 * @returns {Promise<{applied: number[], failed: number[]}>} indices of each
 */
async function applySequentially(items, applyOne) {
  const applied = [];
  const failed = [];

  for (let i = 0; i < items.length; i += 1) {
    try {
      await applyOne(items[i], i);
      applied.push(i);
    } catch (e) {
      console.warn('[aiChat] applySequentially — item failed, continuing', i, e);
      failed.push(i);
    }
  }

  return { applied, failed };
}

/**
 * Build the createAttraction body for an AI suggestion.
 *
 * The resolved Google place, when there is one, supplies the name shown at the
 * top of the text and the coordinates; without one the attraction is saved
 * without a map position rather than at 0,0.
 *
 * @param {object} suggestion the AI's suggestion card ({ title, description })
 * @param {object|null} place a resolved Google Places result, or null
 * @param {string} plannedDate from buildPlannedDate
 */
function buildAttractionPayload(suggestion, place, plannedDate) {
  const placeName = place ? place.name : suggestion.title;
  const payload = {
    title: suggestion.title,
    text: placeName + (suggestion.description ? `\n${suggestion.description}` : ''),
    plannedDate,
  };

  if (place) {
    payload.lat = place.geometry.location.lat();
    payload.lng = place.geometry.location.lng();
    payload.address = place.formatted_address || '';
    payload.placeId = place.place_id;
    // A new place nobody chose an image for: use the admin-approved one, if any.
    payload.fillSharedImage = true;
  }

  return payload;
}

/**
 * Midnight of the given day, in local time.
 *
 * "YYYY-MM-DD" is parsed by hand because `new Date("2026-08-10")` is UTC
 * midnight while `new Date("2026-08-10T00:00:00")` is local -- comparing the
 * two forms shifts a day in any negative-offset timezone.
 */
function startOfLocalDay(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const parts = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) return new Date(+parts[1], +parts[2] - 1, +parts[3]);
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Ids of the AI's city-level area cards covering a hotel stay, so approving the
 * hotel can replace them.
 *
 * The backend merges the AI area and the base-trip suggestion into one
 * `base_trip_accommodation` slot per day, so the status is what tells them
 * apart: only `ai_suggested_area` rows are the AI's to remove. A stay runs from
 * check-in up to but not including check-out -- the checkout day is not a night.
 *
 * @returns {number[]} distinct accommodation ids, empty if the dates are unusable
 */
function suggestedAreaIdsInRange(allRide, checkIn, checkOut) {
  const from = startOfLocalDay(checkIn);
  const to = startOfLocalDay(checkOut);
  if (!from || !to) return [];

  const days = (allRide && allRide.attractionsByDay) || [];
  const ids = [];

  days.forEach((day) => {
    const accommodation = day && day.base_trip_accommodation;
    if (!accommodation || accommodation.status !== 'ai_suggested_area') return;
    if (accommodation.id === undefined || accommodation.id === null) return;

    const dayStart = startOfLocalDay(day.dateTime);
    if (!dayStart || dayStart < from || dayStart >= to) return;
    if (!ids.includes(accommodation.id)) ids.push(accommodation.id);
  });

  return ids;
}

const AI_TYPE_TO_KIND = {
  confirmed_booking: 'booked',
  approved_area: 'suggestion',
  suggested_area: 'suggestion',
};

/**
 * Decide how an accommodation the AI wants removed must be deleted.
 *
 * A real booking and a suggestion go through different endpoints, and only one
 * of them is permanent, so the ride is consulted first: a wrong
 * accommodation_type from the model must not send a real hotel down the
 * permanent path. The model's type is used only when the ride does not carry
 * the accommodation at all.
 *
 * @returns {'booked'|'suggestion'|null} null when it cannot be placed safely
 */
function resolveAccommodationKind(allRide, accommodationId, aiType) {
  const days = (allRide && allRide.attractionsByDay) || [];

  for (const day of days) {
    const candidates = [day && day.accommodation, day && day.base_trip_accommodation];
    for (const acc of candidates) {
      if (!acc || acc.id !== accommodationId) continue;
      return acc.status === 'booked' ? 'booked' : 'suggestion';
    }
  }

  return AI_TYPE_TO_KIND[aiType] || null;
}

/**
 * Body for update_ride_information_api, carrying a new start date.
 *
 * That endpoint replaces the whole ride record and commits the non-date fields
 * before it checks for a booked-accommodation conflict, so every current value
 * has to be resent faithfully -- a dropped title is a wiped title even when the
 * date change is then refused. Returns null rather than a partial payload when
 * the date is unusable.
 *
 * @param {object} ride the record from ride_information_api
 * @param {string} newStartDate "YYYY-MM-DD"
 */
function buildRideDateUpdatePayload(ride, newStartDate) {
  if (!ride) return null;
  const checkIn = toBackendDate(newStartDate);
  if (!checkIn) return null;

  const payload = {
    title: ride.title || '',
    text: ride.text || '',
    photos_albom_link: ride.photos_albom_link || '',
    checkIn,
    number_of_adult: ride.number_of_adult,
    number_of_teen: ride.number_of_teen,
    number_of_child: ride.number_of_child,
    // Compared against the string 'true' server-side, never as a boolean.
    physical_disabilities: String(!!ride.physical_disabilities),
    is_subdomain: String(!!ride.is_white_label),
    language: ride.language || 'hebrew',
  };

  if (ride.top_page_image_url) payload.top_page_image_url = ride.top_page_image_url;
  if (ride.cover_image_url) payload.cover_image_url = ride.cover_image_url;

  return payload;
}

/**
 * Convert the AI's 1-indexed day number to the 0-based dayIndex the
 * add/remove-day endpoints take in their path.
 *
 * Refuses anything that is not a whole day inside the trip: an off-by-one or a
 * stray value here adds or deletes the wrong day, and the endpoints take the
 * index on trust.
 *
 * @param {number} dayNumber 1-indexed, as the model counts days
 * @param {number|null} totalDays days in the trip, when known
 * @returns {number|null} the 0-based index, or null when it cannot be trusted
 */
function dayIndexForDayNumber(dayNumber, totalDays) {
  if (!Number.isInteger(dayNumber) || dayNumber < 1) return null;
  if (Number.isInteger(totalDays) && dayNumber > totalDays) return null;
  return dayNumber - 1;
}

function joinNoteText(current, addition) {
  const base = (current || '').replace(/\s+$/, '');
  return base ? `${base}\n${addition}` : addition;
}

/**
 * Turn the AI's day-note card into the request for the extra-information API.
 *
 * Both the update and the delete endpoint take the note's id, and the update
 * one overwrites title and text together -- so an empty field from the model
 * is filled back from the saved note rather than sent as a blank. A day shows
 * one note, so an "add" on a day that already has one appends to that note.
 *
 * @param {object} update the AI's card ({ operation, note_id, title, text, append_text })
 * @param {object} ctx { rideId, dayDateTime, notes } -- dayDateTime is the
 *   card's day from allRide, notes is rideHeader.ride.rideExtraInformationByDay
 * @returns {{kind: 'add'|'update'|'remove', body: object}|null} null when it cannot be applied safely
 */
function buildDayNoteRequest(update, { rideId, dayDateTime, notes }) {
  const list = notes || [];
  const title = ((update && update.title) || '').trim();
  const text = ((update && update.text) || '').trim();
  const appendText = ((update && update.append_text) || '').trim();

  const updateOf = (note, newTitle, newText) => ({
    kind: 'update',
    body: {
      ride_id: rideId,
      extra_information_id: note.id,
      title: newTitle || note.title || '',
      text: newText,
    },
  });

  switch (update && update.operation) {
    case 'add': {
      if (!dayDateTime || (!title && !text)) return null;
      const existing = list.find(note => note.date === dayDateTime);
      if (existing) return updateOf(existing, '', joinNoteText(existing.text, text || title));
      return { kind: 'add', body: { ride_id: rideId, title, text, date: dayDateTime } };
    }
    case 'update': {
      const note = list.find(n => n.id === update.note_id);
      if (!note) return null;
      let newText = text || note.text || '';
      if (appendText) newText = joinNoteText(newText, appendText);
      return updateOf(note, title, newText);
    }
    case 'remove': {
      const note = list.find(n => n.id === update.note_id);
      if (!note) return null;
      return { kind: 'remove', body: { ride_id: rideId, extra_information_id: note.id } };
    }
    default:
      return null;
  }
}

/**
 * Turn the AI's trip-note card into the request for the extra-information API.
 *
 * A trip note is a ride-level "General Information" entry: the same table as a
 * day note, with no date on it. Three differences from buildDayNoteRequest
 * follow from that, and they are the whole of this function:
 *
 * - a trip can carry MANY of them, so an "add" is always a genuine add. It
 *   never folds into an existing note the way a day's second note does;
 * - with many of them, "update" and "remove" are placed by note id alone --
 *   there is no day to fall back on, so a card without a matching id is
 *   refused rather than guessed at;
 * - no date is sent, which is exactly what makes the new row ride-level. The
 *   add endpoint reads 'date' unconditionally, so the key is present and null
 *   rather than absent -- the same shape the manager's own form sends.
 *
 * sort_order is deliberately not sent: the add endpoint assigns max+1 itself
 * (get_next_sort_order), so a new note lands at the end of the list, and the
 * update endpoint does not touch the field at all.
 *
 * @param {object} update the AI's card ({ operation, note_id, title, text, append_text })
 * @param {object} ctx { rideId, notes } -- notes is rideHeader.ride.rideExtraInformation
 * @returns {{kind: 'add'|'update'|'remove', body: object}|null} null when it cannot be applied safely
 */
function buildRideNoteRequest(update, { rideId, notes }) {
  const list = notes || [];
  const title = ((update && update.title) || '').trim();
  const text = ((update && update.text) || '').trim();
  const appendText = ((update && update.append_text) || '').trim();

  switch (update && update.operation) {
    case 'add': {
      if (!title && !text) return null;
      return { kind: 'add', body: { ride_id: rideId, title, text, date: null } };
    }
    case 'update': {
      const note = list.find(n => n.id === update.note_id);
      if (!note) return null;
      // The endpoint overwrites title and text together, so a field the model
      // left empty is filled back from the saved note rather than sent blank.
      let newText = text || note.text || '';
      if (appendText) newText = joinNoteText(newText, appendText);
      return {
        kind: 'update',
        body: {
          ride_id: rideId,
          extra_information_id: note.id,
          title: title || note.title || '',
          text: newText,
        },
      };
    }
    case 'remove': {
      const note = list.find(n => n.id === update.note_id);
      if (!note) return null;
      return { kind: 'remove', body: { ride_id: rideId, extra_information_id: note.id } };
    }
    default:
      return null;
  }
}

/**
 * Body for update_attraction_api carrying only what the AI changes.
 *
 * That endpoint writes just the fields a form includes, so leaving the rest
 * out keeps them -- the location above all. A card that changes nothing is
 * refused here rather than sent.
 */
function buildAttractionEditBody(update) {
  const body = {};
  const title = ((update && update.new_title) || '').trim();
  const text = ((update && update.new_text) || '').trim();
  const appendText = ((update && update.append_text) || '').trim();
  if (title) body.title = title;
  if (text) body.text = text;
  if (appendText) body.appendText = appendText;
  return Object.keys(body).length ? body : null;
}

/**
 * Partial payload for update_ride_information_api carrying the AI's change to
 * the trip description, and nothing else. That endpoint writes only the keys
 * it is given, so these two are the whole body: no title, no dates, no
 * traveller counts, and therefore no date handling server-side.
 *
 * Both fields are passed through rather than joined here, exactly as
 * buildAttractionEditBody and buildAccommodationEditBody pass theirs: the
 * endpoint appends from the STORED description, which is the only complete
 * copy. The model is shown a copy capped at 2000 characters, so a join made
 * here would silently drop everything past the cap -- and joining from a value
 * read a moment earlier would also lose whatever another tab saved in between.
 *
 * A card that changes nothing is refused here rather than sent.
 */
function buildRideTextUpdatePayload(update) {
  const payload = {};
  const text = ((update && update.new_text) || '').trim();
  const appendText = ((update && update.append_text) || '').trim();
  if (text) payload.text = text;
  if (appendText) payload.appendText = appendText;
  return Object.keys(payload).length ? payload : null;
}

/**
 * The trip's NAME. Ride.title is a String(140) NOT NULL column, so a longer
 * title is not a validation nicety: Postgres raises on commit and the endpoint
 * answers a bodyless 500, which the panel can only report as a generic
 * failure. The card refuses before the call instead.
 */
const MAX_RIDE_TITLE_LENGTH = 140;

/**
 * Read the AI's title card into the one line that can be saved, or say why it
 * cannot be.
 *
 * Whitespace is COLLAPSED rather than refused. A title is one line -- it is
 * rendered in a trip-list row, a page heading, the brochure and the trip
 * reminders, none of which survive a line break -- but a model that wrapped it
 * still wrote the title the manager read on the card, so the break is removed
 * and the card applies. The length is measured after that, so padding does not
 * count towards the limit.
 *
 * @returns {{title: string, refusal: string|null}}
 */
function normalizeRideTitle(update) {
  const raw = (update && update.new_title) || '';
  const title = String(raw).replace(/\s+/g, ' ').trim();
  if (!title) return { title, refusal: 'invalid_title' };
  if (title.length > MAX_RIDE_TITLE_LENGTH) return { title, refusal: 'title_too_long' };
  return { title, refusal: null };
}

/**
 * Partial payload for update_ride_information_api carrying the AI's new trip
 * title, and nothing else. That endpoint writes only the keys it is given, so
 * this one key is the whole body: the description, the dates and the traveller
 * counts are left exactly as they are.
 *
 * There is no append half, unlike the description and the attraction cards: a
 * name is replaced, never added to, so the model sends the whole of it.
 *
 * A card that cannot be saved is refused here rather than sent.
 */
function buildRideTitleUpdatePayload(update) {
  const { title, refusal } = normalizeRideTitle(update);
  return refusal ? null : { title };
}

/**
 * Why buildRideTitleUpdatePayload refused, so the panel can say which of the
 * two problems it is -- "nothing came through" and "that name is too long"
 * send the manager to different places.
 *
 * @returns {'invalid_title'|'title_too_long'|null} null when the card is fine
 */
function rideTitleRefusal(update) {
  return normalizeRideTitle(update).refusal;
}

/**
 * The language the trip is written in -- one of the stored codes the ride form
 * offers ('hebrew', 'english', 'spanish', ...). It decides the direction and
 * labels of the brochure, the language of the e-mails sent about the trip, and
 * the interface language the ride page opens in.
 *
 * The model is asked for the stored code, but a capitalised or padded one
 * ("English ") is still the language the manager read on the card, so it is
 * lower-cased and trimmed rather than refused. Anything that is then not one of
 * RIDE_LANGUAGE_CODES is refused: the column takes any string, and a value the
 * ride form cannot show would leave the manager's own form blank.
 *
 * @returns {{language: string, refusal: 'invalid_language'|'unknown_language'|null}}
 */
function normalizeRideLanguage(update) {
  const raw = (update && update.new_language) || '';
  const language = String(raw).trim().toLowerCase();
  if (!language) return { language, refusal: 'invalid_language' };
  if (!RIDE_LANGUAGE_CODES.includes(language)) return { language, refusal: 'unknown_language' };
  return { language, refusal: null };
}

/**
 * Partial payload for update_ride_information_api carrying the new ride
 * language and nothing else, so the title, description, dates and traveller
 * counts are left as they are. A card that cannot be saved is refused here.
 */
function buildRideLanguageUpdatePayload(update) {
  const { language, refusal } = normalizeRideLanguage(update);
  return refusal ? null : { language };
}

function rideLanguageRefusal(update) {
  return normalizeRideLanguage(update).refusal;
}

/**
 * Whether saving this ride language flips the interface the manager is looking
 * at. The ride page watches rideHeader.ride.language and sets the UI language
 * from it, and when that changes the Google Maps script language it reloads the
 * page -- which closes the chat, whose conversation lives only in memory. The
 * card warns before the press.
 *
 * Only Hebrew and English are UI languages; any other ride language leaves the
 * interface as it is (language.js ignores it), so it never reloads.
 *
 * @param {string} currentUiLanguage 'he' | 'en'
 * @param {string} newRideLanguage a stored ride language code
 */
function rideLanguageSwitchesInterface(currentUiLanguage, newRideLanguage) {
  const ui = { hebrew: 'he', english: 'en' }[newRideLanguage];
  return !!ui && ui !== currentUiLanguage;
}

// The order "Accept all" applies content cards in. Removals and hotel date
// changes go first so a new hotel is not refused by the double-booking guard
// for nights an old stay still held, and areas precede hotels, which then
// replace them. The prefixes
// are the panel's addedSet keys.
const CONTENT_ORDER = [
  { key: 'removals', prefix: 'x' },
  { key: 'accommodation_removals', prefix: 'ar' },
  { key: 'accommodation_updates', prefix: 'hu' },
  { key: 'reschedules', prefix: 'r' },
  { key: 'attraction_updates', prefix: 'ae' },
  { key: 'area_updates', prefix: 'u' },
  { key: 'areas', prefix: 'a' },
  { key: 'hotel_suggestions', prefix: 'h' },
  { key: 'suggestions', prefix: 's' },
  { key: 'day_note_updates', prefix: 'dn' },
  // Last: the trip description, the trip's own notes, its name and its
  // language are their own rows and nothing else in a reply depends on them.
  // They stay at the end of the list because planAcceptAll destructures this
  // array positionally -- a new entry goes after these, never between them.
  { key: 'ride_text_updates', prefix: 'rt' },
  { key: 'ride_note_updates', prefix: 'rn' },
  { key: 'ride_title_updates', prefix: 'tt' },
  // The very last, and on purpose: switching between Hebrew and English
  // reloads the ride page (see rideLanguageSwitchesInterface), so every other
  // card of the batch has to have been applied before it.
  { key: 'ride_language_updates', prefix: 'rl' },
];

// Cards that cannot be applied until the user picks a Google place.
const NEEDS_PLACE = ['s', 'a', 'h', 'u'];

function countBy(items, keyOf) {
  const counts = new Map();
  items.forEach((item) => {
    const k = keyOf(item);
    if (k === undefined || k === null) return;
    counts.set(k, (counts.get(k) || 0) + 1);
  });
  return counts;
}

// A stay runs from check-in up to, not including, check-out.
function staysOverlap(a, b) {
  if (!a.check_in || !a.check_out || !b.check_in || !b.check_out) return false;
  return a.check_in < b.check_out && a.check_out > b.check_in;
}

/**
 * Decide what "Accept all" applies from one AI reply, and in what order.
 *
 * Day and date changes renumber or shift everything after them, so a reply
 * carrying any still-pending one applies only those -- day changes from the
 * last day back, so each still names the day it meant -- and holds the rest
 * (the AI is told to send content in its next reply). Otherwise content runs
 * in CONTENT_ORDER. A card is left for the user to press when two cards touch
 * the same thing, when hotels or areas overlap (they are options to choose
 * from), or when it still needs a place picked.
 *
 * @param {object} msg the assistant message with its card arrays
 * @param {object} state { isDone(prefix, index), hasPlace(prefix, index) }
 * @returns {{steps: object[], skipped: object[], removals: object[]}}
 *   steps and removals are { prefix, index, card }; skipped adds a reason
 */
function planAcceptAll(msg, { isDone, hasPlace }) {
  const pendingOf = (key, prefix) => ((msg && msg[key]) || [])
    .map((card, index) => ({ prefix, index, card }))
    .filter(({ index }) => !isDone(prefix, index));

  const dayChanges = pendingOf('day_updates', 'du');
  const dateChanges = pendingOf('ride_date_updates', 'rd');
  const content = CONTENT_ORDER.map(({ key, prefix }) => pendingOf(key, prefix));

  if (dayChanges.length || dateChanges.length) {
    const ordered = dayChanges.slice().sort((a, b) => {
      if (b.card.day !== a.card.day) return b.card.day - a.card.day;
      // Same day: insert first, so the removal still hits the original day.
      return (a.card.operation === 'remove' ? 1 : 0) - (b.card.operation === 'remove' ? 1 : 0);
    });
    return {
      steps: ordered.concat(dateChanges),
      skipped: [].concat(...content).map(st => ({ ...st, reason: 'after_day_changes' })),
      removals: ordered.filter(st => st.card.operation === 'remove'),
    };
  }

  const [removals, accRemovals, accUpdates, moves, edits, , areas, hotels, , notes, rideTexts,
    rideNotes, rideTitles, rideLanguages] = content;

  const removedIds = new Set(removals.map(st => st.card.attraction_id));
  const touches = countBy([...removals, ...moves, ...edits], st => st.card.attraction_id);
  const movesById = countBy(moves, st => st.card.attraction_id);
  const editsById = countBy(edits, st => st.card.attraction_id);
  const accById = countBy([...accRemovals, ...accUpdates], st => st.card.accommodation_id);
  const notesByDay = countBy(notes, st => st.card.day);
  const notesById = countBy(notes, st => st.card.note_id);
  const rideNotesById = countBy(rideNotes, st => st.card.note_id);

  const conflicts = (st) => {
    const { prefix, card } = st;
    if (prefix === 'x' || prefix === 'r' || prefix === 'ae') {
      const id = card.attraction_id;
      if (removedIds.has(id) && touches.get(id) > 1) return true;
      if (prefix === 'r' && movesById.get(id) > 1) return true;
      if (prefix === 'ae' && editsById.get(id) > 1) return true;
    }
    if (prefix === 'ar' || prefix === 'hu') return accById.get(card.accommodation_id) > 1;
    if (prefix === 'dn') {
      return notesByDay.get(card.day) > 1 || (card.note_id != null && notesById.get(card.note_id) > 1);
    }
    // The description is one field, and the prompt allows one card for it. If
    // the model sends two anyway, applying both in a row would have the second
    // silently overwrite the first -- so neither runs and the manager chooses.
    if (prefix === 'rt') return rideTexts.length > 1;
    // A trip may hold many notes, so two "add" cards in one reply are two
    // different notes and both run. Two cards naming the SAME note_id are
    // not: applying both in a row would have the second overwrite the first,
    // or update a note the other card just deleted.
    if (prefix === 'rn') return card.note_id != null && rideNotesById.get(card.note_id) > 1;
    // The trip's name is one field, and the prompt allows one card for it --
    // the same shape as the description above, and refused the same way.
    if (prefix === 'tt') return rideTitles.length > 1;
    // One field again: two language cards are two different answers.
    if (prefix === 'rl') return rideLanguages.length > 1;
    return false;
  };
  const overlapsAnother = (st, group) => group.some(other => other !== st && staysOverlap(st.card, other.card));

  const steps = [];
  const skipped = [];
  [].concat(...content).forEach((st) => {
    let reason = null;
    if (conflicts(st)) reason = 'conflict';
    else if ((st.prefix === 'h' && overlapsAnother(st, hotels)) || (st.prefix === 'a' && overlapsAnother(st, areas))) reason = 'alternatives';
    // A title the endpoint would refuse -- blank, or past the column's 140
    // characters -- is kept out of the batch rather than run and failed. Like
    // 'needs_place' it is skipped and NOT outstanding, so one unusable rename
    // cannot leave a batch permanently unfinished and block the next turn.
    else if (st.prefix === 'tt' && rideTitleRefusal(st.card)) reason = 'unsaveable';
    else if (st.prefix === 'rl' && rideLanguageRefusal(st.card)) reason = 'unsaveable';
    else if (NEEDS_PLACE.includes(st.prefix) && !hasPlace(st.prefix, st.index)) reason = 'needs_place';

    if (reason) skipped.push({ ...st, reason });
    else steps.push(st);
  });

  return {
    steps,
    skipped,
    removals: steps.filter(st => st.prefix === 'x' || st.prefix === 'ar'
      || ((st.prefix === 'dn' || st.prefix === 'rn') && st.card.operation === 'remove')),
  };
}

// The two card kinds that carry a stay, and so arrive as alternatives to each
// other: approving one settles the choice for the nights it covers.
const STAY_PREFIXES = { a: 'areas', h: 'hotel_suggestions' };

/**
 * What of one reply the manager has still not dealt with. Auto-continue uses
 * this to decide a batch is finished.
 *
 * planAcceptAll's `steps` alone is the wrong answer for stays. The prompt has
 * the model offer 2-3 hotels per area, so hotel cards arrive as overlapping
 * alternatives, which planAcceptAll deliberately keeps out of `steps`. Two
 * shapes then read wrongly:
 *
 * - one area, two options: approve one and its loser stops overlapping
 *   anything, so it turns into an ordinary step and the batch looks unfinished
 *   for ever -- the manager cannot clear it either, since the second stay is
 *   refused as a double booking;
 * - three areas, three options each: approve one hotel and every remaining
 *   card still overlaps a sibling, so `steps` is empty and the batch looks
 *   finished with two areas unslept.
 *
 * Outstanding is therefore the plan's steps, minus any stay card overlapping
 * one the manager has already applied -- a choice they have made -- plus the
 * alternatives the plan skipped whose group nobody has chosen from yet.
 *
 * One asymmetry here is deliberate and worth naming, because it looks like an
 * oversight. A card skipped as `needs_place` is NOT outstanding: one stop
 * whose Google lookup failed must not stall gate 2's area-by-area
 * continuation for ever. But `planAcceptAll` tests `alternatives` before
 * `needs_place`, so two overlapping stay options whose lookups both failed
 * are classed `alternatives`, are both outstanding, and the batch can never
 * reach zero -- the opposite call, on cards the same test would otherwise
 * excuse. It is narrow (both options unresolvable, and the manager never
 * touches the manual picker) and it fails closed: the gate simply does not
 * auto-cross and the manager types. Left as it is rather than reordered,
 * because reordering would make a chosen-from group of alternatives look
 * applied when only its unresolvable members remain.
 *
 * @param {object} msg the assistant message with its card arrays
 * @param {object} plan the planAcceptAll result for that message
 * @param {object} state { isDone(prefix, index) }
 * @returns {object[]} the steps-shaped cards still waiting on the manager
 */
function outstandingCards(msg, plan, { isDone }) {
  const appliedStays = {};
  Object.keys(STAY_PREFIXES).forEach((prefix) => {
    appliedStays[prefix] = ((msg && msg[STAY_PREFIXES[prefix]]) || [])
      .filter((card, index) => isDone(prefix, index));
  });
  const settled = st => !!appliedStays[st.prefix]
    && appliedStays[st.prefix].some(card => staysOverlap(st.card, card));

  return plan.steps.filter(st => !settled(st))
    .concat(plan.skipped.filter(st => st.reason === 'alternatives' && !settled(st)));
}

/**
 * Body for update_accommodation_api carrying only what the AI changes.
 *
 * That endpoint now writes just the fields a form includes, so the booking
 * confirmation, vouchers and image the AI never sees are kept. Dates go in
 * the backend's DD-MM-YYYY; a date that cannot be read refuses the whole card
 * rather than sending the rest of it.
 */
function buildAccommodationEditBody(update) {
  const body = {};
  const name = ((update && update.new_name) || '').trim();
  const text = ((update && update.new_text) || '').trim();
  const appendText = ((update && update.append_text) || '').trim();
  const dates = [['check_in', 'checkIn'], ['check_out', 'checkOut']];

  for (const [from, to] of dates) {
    const value = ((update && update[from]) || '').trim();
    if (!value) continue;
    const backendDate = toBackendDate(value);
    if (!backendDate) return null;
    body[to] = backendDate;
  }
  if (name) body.name = name;
  if (text) body.text = text;
  if (appendText) body.appendText = appendText;
  return Object.keys(body).length ? body : null;
}

module.exports = {
  buildPlannedDate,
  applySequentially,
  buildAttractionPayload,
  suggestedAreaIdsInRange,
  resolveAccommodationKind,
  buildRideDateUpdatePayload,
  dayIndexForDayNumber,
  buildDayNoteRequest,
  buildRideNoteRequest,
  buildAttractionEditBody,
  planAcceptAll,
  outstandingCards,
  buildAccommodationEditBody,
  buildRideTextUpdatePayload,
  normalizeRideTitle,
  buildRideTitleUpdatePayload,
  rideTitleRefusal,
  MAX_RIDE_TITLE_LENGTH,
  normalizeRideLanguage,
  buildRideLanguageUpdatePayload,
  rideLanguageRefusal,
  rideLanguageSwitchesInterface,
};
